// Test ids (the `data-cy` attributes) shared by the app and the Cypress tests.
// Rules (AGENTS.md, section 10): a component sets `data-cy={TEST_IDS.<AREA>.<NAME>}`; a Cypress spec or page object
// selects it with `dataCy(TEST_IDS.<AREA>.<NAME>)` (frontend-e2e/utils/selectors.ts). Never type the id string twice.

export const TEST_IDS = {
    ADMIN_CENTER: {
        COMMON_MULTI_SEARCH_SELECT_POPUP: 'common-multi-search-select-popup',
        COMMON_MULTI_SEARCH_SELECT_SELECTED_COUNT: 'common-multi-search-select-selected-count',
        multiSearchSelectOption: (value: string | number) => `common-multi-search-select-option-${value}`,
    },
    ASSESSOR: {
        ASSESSOR_BACK_BUTTON: 'assessor-back-button',
        ASSESSOR_CLOSE_BUTTON: 'assessor-close-button',
        ASSESSOR_COMMENTS_INPUT: 'assessor-comments-input',
        ASSESSOR_EVALUATION_ACTION_SWITCH: 'assessor-evaluation-action-switch',
        ASSESSOR_EVALUATION_METHOD_SELECT: 'assessor-evaluation-method-select',
        ASSESSOR_SAVE_DRAFT_BUTTON: 'assessor-save-draft-button',
        EVALUATION_INTRO_ASSESSEE: 'evaluation-intro-assessee',
        EVALUATION_INTRO_ATTEMPTS: 'evaluation-intro-attempts',
        EVALUATION_INTRO_DATE: 'evaluation-intro-date',
        EVALUATION_INTRO_GUIDELINES: 'evaluation-intro-guidelines',
        EVALUATION_INTRO_TEXT: 'evaluation-intro-text',
        EVALUATION_INTRO_TITLE: 'evaluation-intro-title',
        EVALUATION_OUTRO_DATE: 'evaluation-outro-date',
        EVALUATION_OUTRO_METHOD: 'evaluation-outro-method',
        EVALUATION_OUTRO_STATUS: 'evaluation-outro-status',
        EVALUATION_OUTRO_TITLE: 'evaluation-outro-title',
        EXEMPTION_COMMENT_INPUT: 'exemption-comment-input',
        EXEMPTION_EVIDENCE_INPUT: 'exemption-evidence-input',
        taskRating: (taskIndex: number, ratingIndex: number) => `assessor-task-${taskIndex}-rating-${ratingIndex}`,
        markAllRating: (ratingIndex: number) => `assessor-mark-all-rating-${ratingIndex}`,
    },
    AUTH: {
        LOGIN_BUTTON: 'login-button',
        LOGIN_INPUT: 'login-input',
        PASSW_CHANGE_BUTTON: 'passw-change-button',
        REGISTRATION_BUTTON: 'registration-button',
        REGISTRATION_INPUT: 'registration-input',
        passwordInput: (fieldName: string) => `password-input-${fieldName}`,
    },
    CREATOR: {
        ADD_ELEMENT_BUTTON: 'add-element-button',
        ADD_PATHWAY_BUTTON: 'add-pathway-button',
        ARCHIVE_ELEMENT_CANCEL: 'archive-element-cancel',
        ARCHIVE_ELEMENT_COMMENTS: 'archive-element-comments',
        ARCHIVE_ELEMENT_CONFIRM: 'archive-element-confirm',
        ARCHIVE_ELEMENT_MODAL: 'archive-element-modal',
        ARCHIVE_ELEMENT_TITLE: 'archive-element-title',
        ARCHIVE_PATHWAY_CANCEL: 'archive-pathway-cancel',
        ARCHIVE_PATHWAY_COMMENTS: 'archive-pathway-comments',
        ARCHIVE_PATHWAY_CONFIRM: 'archive-pathway-confirm',
        ARCHIVE_PATHWAY_TITLE: 'archive-pathway-title',
        BROWSE_PATHWAY_ELEMENTS_ADD: 'browse-pathway-elements-add',
        BROWSE_PATHWAY_ELEMENTS_CANCEL: 'browse-pathway-elements-cancel',
        BROWSE_PATHWAY_ELEMENTS_SEARCH: 'browse-pathway-elements-search',
        BROWSE_PATHWAY_ELEMENTS_SELECTED_COUNT: 'browse-pathway-elements-selected-count',
        BROWSE_PATHWAY_ELEMENTS_SUBTITLE: 'browse-pathway-elements-subtitle',
        BROWSE_PATHWAY_ELEMENTS_TITLE: 'browse-pathway-elements-title',
        CHANGE_ELEMENT_TYPE_CANCEL: 'change-element-type-cancel',
        CHANGE_ELEMENT_TYPE_CONFIRM: 'change-element-type-confirm',
        CHANGE_ELEMENT_TYPE_MODAL: 'change-element-type-modal',
        DELETE_SUBTASK_CANCEL: 'delete-subtask-cancel',
        DELETE_SUBTASK_CONFIRM: 'delete-subtask-confirm',
        DELETE_SUBTASK_MODAL: 'delete-subtask-modal',
        ELEMENTS_SEARCH_INPUT: 'elements-search-input',
        ELEMENT_CREATED_BY_SELECT: 'element-created-by-select',
        ELEMENT_CREATED_FOR_SELECT: 'element-created-for-select',
        ELEMENT_CREATOR_REFERENCE_INPUT: 'element-creator-reference-input',
        ELEMENT_FILE_INPUT: 'element-file-input',
        ELEMENT_LEARNING_OBJECTIVE_INPUT: 'element-learning-objective-input',
        ELEMENT_PUBLISH: 'element-publish',
        ELEMENT_RESOURCE_BUTTON: 'element-resource-button',
        ELEMENT_RESOURCE_INPUT: 'element-resource-input',
        ELEMENT_RESOURCE_LABEL: 'element-resource-label',
        ELEMENT_RESOURCE_PREVIEW: 'element-resource-preview',
        ELEMENT_RUN_TIME_MINUTES: 'element-run-time-minutes',
        ELEMENT_RUN_TIME_SECONDS: 'element-run-time-seconds',
        ELEMENT_SAVE: 'element-save',
        ELEMENT_THUMBNAIL_BUTTON: 'element-thumbnail-button',
        ELEMENT_TITLE_INPUT: 'element-title-input',
        ELEMENT_TYPE_SELECT: 'element-type-select',
        PATHWAYS_SEARCH_INPUT: 'pathways-search-input',
        PATHWAY_BROWSE_ELEMENTS_BUTTON: 'pathway-browse-elements-button',
        PATHWAY_CANCEL: 'pathway-cancel',
        PATHWAY_CATEGORY_SELECT: 'pathway-category-select',
        PATHWAY_CERTIFICATE_TEMPLATE_SELECT: 'pathway-certificate-template-select',
        PATHWAY_CERTIFICATE_TEXT_INPUT: 'pathway-certificate-text-input',
        PATHWAY_COMPLETION_TYPE_SELECT: 'pathway-completion-type-select',
        PATHWAY_COVER_IMAGE: 'pathway-cover-image',
        PATHWAY_COVER_IMAGE_UPLOAD_BUTTON: 'pathway-cover-image-upload-button',
        PATHWAY_CREATED_BY_SELECT: 'pathway-created-by-select',
        PATHWAY_CREATED_FOR_SELECT: 'pathway-created-for-select',
        PATHWAY_CREATOR_REFERENCE_INPUT: 'pathway-creator-reference-input',
        PATHWAY_ID_INPUT: 'pathway-id-input',
        PATHWAY_PASSING_PERCENTAGE_SELECT: 'pathway-passing-percentage-select',
        PATHWAY_PUBLISH: 'pathway-publish',
        PATHWAY_SAVE: 'pathway-save',
        PATHWAY_SHORT_DESCRIPTION_INPUT: 'pathway-short-description-input',
        PATHWAY_TITLE_INPUT: 'pathway-title-input',
        PATHWAY_TYPE_SELECT: 'pathway-type-select',
        PATHWAY_VIDEO_SEEK_SWITCH: 'pathway-video-seek-switch',
        PUBLISH_ELEMENT_CANCEL: 'publish-element-cancel',
        PUBLISH_ELEMENT_CONFIRM: 'publish-element-confirm',
        PUBLISH_ELEMENT_CREATOR_COMMENTS: 'publish-element-creator-comments',
        PUBLISH_ELEMENT_REVISION_NOTES: 'publish-element-revision-notes',
        PUBLISH_PATHWAY_CANCEL: 'publish-pathway-cancel',
        PUBLISH_PATHWAY_CONFIRM: 'publish-pathway-confirm',
        PUBLISH_PATHWAY_CREATOR_COMMENTS: 'publish-pathway-creator-comments',
        PUBLISH_PATHWAY_REVISION_NOTES: 'publish-pathway-revision-notes',
        QUESTIONS_CSV_UPLOAD: 'questions-csv-upload',
        QUESTIONS_CSV_UPLOAD_ERROR: 'questions-csv-upload-error',
        QUESTIONS_CSV_UPLOAD_SUBTITLE: 'questions-csv-upload-subtitle',
        QUESTIONS_CSV_UPLOAD_TITLE: 'questions-csv-upload-title',
        QUESTION_ANSWERS_SUMMARY: 'question-answers-summary',
        QUESTION_CORRECT_FEEDBACK_INPUT: 'question-correct-feedback-input',
        QUESTION_FINAL_TOTAL: 'question-final-total',
        QUESTION_FINAL_TO_SELECT: 'question-final-to-select',
        QUESTION_INTRODUCTION_INPUT: 'question-introduction-input',
        QUESTION_MIN_ANSWERS_SELECT: 'question-min-answers-select',
        QUESTION_PARTIALLY_CORRECT_FEEDBACK_INPUT: 'question-partially-correct-feedback-input',
        QUESTION_PRACTICE_TOTAL: 'question-practice-total',
        QUESTION_PRACTICE_TO_SELECT: 'question-practice-to-select',
        QUESTION_PROMPT_INPUT: 'question-prompt-input',
        QUESTION_QUIZ_TYPE_SELECT: 'question-quiz-type-select',
        QUESTION_REQUIRED_ANSWER_TYPE_SELECT: 'question-required-answer-type-select',
        QUESTION_TIME_LIMIT_SELECT: 'question-time-limit-select',
        QUESTION_TOTAL_ANSWERS_SELECT: 'question-total-answers-select',
        QUESTION_TYPE_SELECT: 'question-type-select',
        QUESTION_WRONG_FEEDBACK_INPUT: 'question-wrong-feedback-input',
        RATING_SCALE_CLOSE: 'rating-scale-close',
        RATING_SCALE_MODAL: 'rating-scale-modal',
        TAGGING_ACCREDITATIONS_SELECT: 'tagging-accreditations-select',
        TAGGING_COMPETENCES_SELECT: 'tagging-competences-select',
        TAGGING_DEPARTMENTS_LEVELS_SELECT: 'tagging-departments-levels-select',
        TAGGING_RANKS_SELECT: 'tagging-ranks-select',
        TAGGING_REGULATIONS_SELECT: 'tagging-regulations-select',
        TAGGING_VESSELS_SELECT: 'tagging-vessels-select',
        TASK_ADD_SUBTASK: 'task-add-subtask',
        TASK_EDIT_SUBTASK_CANCEL: 'task-edit-subtask-cancel',
        TASK_EDIT_SUBTASK_DESCRIPTION_INPUT: 'task-edit-subtask-description-input',
        TASK_EDIT_SUBTASK_GUIDELINE_INPUT: 'task-edit-subtask-guideline-input',
        TASK_EDIT_SUBTASK_SAVE: 'task-edit-subtask-save',
        TASK_PREVIEW_BUTTON: 'task-preview-button',
        TASK_PREVIEW_MODAL: 'task-preview-modal',
        TASK_RATING_SCALE_SELECT: 'task-rating-scale-select',
        TASK_SUBTASK_CANCEL: 'task-subtask-cancel',
        TASK_SUBTASK_DESCRIPTION: 'task-subtask-description',
        TASK_SUBTASK_DESCRIPTION_INPUT: 'task-subtask-description-input',
        TASK_SUBTASK_EDIT: 'task-subtask-edit',
        TASK_SUBTASK_GUIDELINE: 'task-subtask-guideline',
        TASK_SUBTASK_GUIDELINE_INPUT: 'task-subtask-guideline-input',
        TASK_SUBTASK_ITEM: 'task-subtask-item',
        TASK_SUBTASK_REMOVE: 'task-subtask-remove',
        TASK_SUBTASK_SAVE: 'task-subtask-save',
        TASK_VIEW_SCALE_DETAILS: 'task-view-scale-details',
        questionAnswerSwitch: (number: number) => `question-answer-switch-${number}`,
        questionVariationInput: (number: number) => `question-variation-input-${number}`,
    },
    // Admin center: fleet and vessel dashboards
    DASHBOARDS: {
        MANDATORY_LEARNING_STATUS_EXPAND: 'mandatory-learning-status-expand',
        MANDATORY_LEARNING_STATUS_TABLE: 'mandatory-learning-status-table',
        VESSEL_NAME: 'vessel-name',
    },
    // Learning pages, learning overview, quizzes, tasks
    LEARNING: {
        ADAPTIVE_QUIZ_ANSWER: 'adaptive-quiz-answer',
        ADAPTIVE_QUIZ_ANSWERS: 'adaptive-quiz-answers',
        ADAPTIVE_QUIZ_QUESTION: 'adaptive-quiz-question',
        ADAPTIVE_QUIZ_SUBMIT: 'adaptive-quiz-submit',
        ADAPTIVE_QUIZ_TIMER: 'adaptive-quiz-timer',
        ADAPTIVE_QUIZ_TIMER_ICON: 'adaptive-quiz-timer-icon',
        ADAPTIVE_QUIZ_TIMER_LEFT: 'adaptive-quiz-timer-left',
        ADAPTIVE_QUIZ_TIMER_TOTAL: 'adaptive-quiz-timer-total',
        ADAPTIVE_QUIZ_TIMER_WARNING_ICON: 'adaptive-quiz-timer-warning-icon',
        CARD_PILL_DUE_SOON: 'card-pill-due-soon',
        CARD_PILL_FOCUS: 'card-pill-focus',
        CARD_PILL_OVERDUE: 'card-pill-overdue',
        CARD_PILL_REFRESHER: 'card-pill-refresher',
        CARD_PILL_UPDATED: 'card-pill-updated',
        COMPLETED_PATHWAY_OUTRO: 'completed-pathway-outro',
        EVALUATION_LOG_ENTRY: 'evaluation-log-entry',
        EVALUATION_LOG_ENTRY_DATE: 'evaluation-log-entry-date',
        EVALUATION_LOG_ENTRY_PERSON: 'evaluation-log-entry-person',
        EVALUATION_LOG_ENTRY_STATUS: 'evaluation-log-entry-status',
        EVALUATION_LOG_ENTRY_TITLE: 'evaluation-log-entry-title',
        EVALUATION_LOG_EVIDENCE_NAME: 'evaluation-log-evidence-name',
        EVALUATION_LOG_EVIDENCE_PREVIEW: 'evaluation-log-evidence-preview',
        EVALUATION_LOG_PRINT_BUTTON: 'evaluation-log-print-button',
        EVALUATION_LOG_RATING: 'evaluation-log-rating',
        EVALUATION_LOG_TASK: 'evaluation-log-task',
        EXIT_PATHWAY: 'exit-pathway',
        FINAL_QUIZ_ANSWER: 'final-quiz-answer',
        FINAL_QUIZ_ANSWERS: 'final-quiz-answers',
        FINAL_QUIZ_FINISH: 'final-quiz-finish',
        FINAL_QUIZ_NEXT: 'final-quiz-next',
        FINAL_QUIZ_PREVIOUS: 'final-quiz-previous',
        FINAL_QUIZ_QUESTION: 'final-quiz-question',
        FINAL_QUIZ_START: 'final-quiz-start',
        FINAL_QUIZ_SUBMIT: 'final-quiz-submit',
        FINAL_QUIZ_TIMER: 'final-quiz-timer',
        FINAL_QUIZ_TIMER_ICON: 'final-quiz-timer-icon',
        FINAL_QUIZ_TIMER_LEFT: 'final-quiz-timer-left',
        FINAL_QUIZ_TIMER_TOTAL: 'final-quiz-timer-total',
        FINAL_QUIZ_TIMER_WARNING_ICON: 'final-quiz-timer-warning-icon',
        INFO_BOX: 'info-box',
        INFO_BOX_ASSIGNED: 'info-box-assigned',
        INFO_BOX_DESCRIPTION: 'info-box-description',
        INFO_BOX_DUE: 'info-box-due',
        LEARNING_CARD: 'learning-card',
        LEARNING_CARD_BOOKMARK: 'learning-card-bookmark',
        LEARNING_CARD_BUTTON: 'learning-card-button',
        LEARNING_CARD_DATE: 'learning-card-date',
        LEARNING_CARD_TITLE: 'learning-card-title',
        LEARNING_OVERVIEW_TITLE: 'learning-overview-title',
        LEARNING_SEARCH_INPUT: 'learning-search-input',
        LEARNING_SECTION: 'learning-section',
        LEARNING_SECTION_HEADING: 'learning-section-heading',
        LEARNING_SECTION_SUBHEADER: 'learning-section-subheader',
        LEARNING_TAB_BOOKMARKED: 'learning-tab-bookmarked',
        LEARNING_TAB_COMPLETED: 'learning-tab-completed',
        LEARNING_TAB_IN_PROGRESS: 'learning-tab-in-progress',
        LEARNING_TAB_OVERDUE: 'learning-tab-overdue',
        LEARNING_TAB_OVERDUE_COUNTER: 'learning-tab-overdue-counter',
        LOCKED_CONTENT: 'locked-content',
        LOCKED_CONTENT_BUTTON: 'locked-content-button',
        LOCKED_CONTENT_DESCRIPTION: 'locked-content-description',
        LOCKED_CONTENT_TITLE: 'locked-content-title',
        NO_ATTEMPTS_LEFT_OUTRO: 'no-attempts-left-outro',
        NO_ATTEMPTS_LEFT_RESTART_PATHWAY: 'no-attempts-left-restart-pathway',
        PATHWAY_ELEMENT: 'pathway-element',
        PATHWAY_FINAL_QUIZ: 'pathway-final-quiz',
        PDF_READ_CONFIRMATION: 'pdf-read-confirmation',
        PRACTICE_QUIZ_ANSWER: 'practice-quiz-answer',
        PRACTICE_QUIZ_ANSWERS: 'practice-quiz-answers',
        PRACTICE_QUIZ_ANSWER_CORRECT_ICON: 'practice-quiz-answer-correct-icon',
        PRACTICE_QUIZ_ANSWER_INCORRECT_ICON: 'practice-quiz-answer-incorrect-icon',
        PRACTICE_QUIZ_ANSWER_SELECTED_ICON: 'practice-quiz-answer-selected-icon',
        PRACTICE_QUIZ_FINISH: 'practice-quiz-finish',
        PRACTICE_QUIZ_NEXT: 'practice-quiz-next',
        PRACTICE_QUIZ_QUESTION: 'practice-quiz-question',
        PRACTICE_QUIZ_START: 'practice-quiz-start',
        PRACTICE_QUIZ_SUBMIT: 'practice-quiz-submit',
        QUIZ_SCORE_CURRENT: 'quiz-score-current',
        QUIZ_SCORE_HIGHEST: 'quiz-score-highest',
        QUIZ_SCORE_REQUIRED: 'quiz-score-required',
        RATE_FEEDBACK_CLOSE: 'rate-feedback-close',
        RESTART_PATHWAY_CANCEL: 'restart-pathway-cancel',
        RESTART_PATHWAY_CONFIRM: 'restart-pathway-confirm',
        RESTART_PATHWAY_TITLE: 'restart-pathway-title',
        RETAKE_FINAL_QUIZ: 'retake-final-quiz',
        UPLOAD_EVIDENCE_MARK_ALL_RATING: 'upload-evidence-mark-all-rating',
        UPLOAD_EVIDENCE_TASK: 'upload-evidence-task',
        UPLOAD_EVIDENCE_TASK_RATING: 'upload-evidence-task-rating',
        YOUTUBE_NOTICE_BANNER: 'youtube-notice-banner',
    },
    NOTIFICATIONS: {
        CARD_INFO_DATES_ASSIGNED: 'card-info-dates-assigned',
        CARD_INFO_DATES_STATUS: 'card-info-dates-status',
        NOTIFICATIONS_SEARCH: 'notifications-search',
        NOTIFICATION_CARD: 'notification-card',
        NOTIFICATION_DETAILS_TOGGLER: 'notification-details-toggler',
        NOTIFICATION_OPEN_BUTTON: 'notification-open-button',
        NOTIFICATION_READ_TOGGLE: 'notification-read-toggle',
        TAG_PILL: 'tag-pill',
        TAG_PILL_ICON: 'tag-pill-icon',
        TAG_PILL_TEXT: 'tag-pill-text',
        TAG_PILL_WITH_ICON: 'tag-pill-with-icon',
    },
    PROFILE: {
        BTN_CANCEL_EDIT: 'btn-cancel-edit',
        BTN_EDIT_PROFILE: 'btn-edit-profile',
        BTN_SUBMIT_FOR_APPROVAL: 'btn-submit-for-approval',
        CHANGE_PASSWORD_TRIGGER: 'change-password-trigger',
        IMPORTED_LEARNINGS_SEARCH: 'imported-learnings-search',
        IMPORTED_LEARNINGS_TABLE: 'imported-learnings-table',
        LEARNING_RESULTS_SEARCH: 'learning-results-search',
        LEARNING_RESULTS_TABLE: 'learning-results-table',
        NOTIFICATIONS_BELL: 'notifications-bell',
        NOTIFICATIONS_COUNT: 'notifications-count',
        PROFILE_BAR: 'profile-bar',
        SUBMITTED_CHANGE_NEW_VALUE: 'submitted-change-new-value',
        SUBMITTED_CHANGE_OLD_VALUE: 'submitted-change-old-value',
        TAB_MY_ASSESSMENT: 'tab-my-assessment',
        TAB_MY_IMPORTED_LEARNINGS: 'tab-my-imported-learnings',
        TAB_MY_LEARNING: 'tab-my-learning',
        profileMenuLink: (name: string) => `profile-menu-${name}`,
    },
    SETTINGS: {
        BTN_REQUEST_DATA_DELETION: 'btn-request-data-deletion',
        CONSENT_ITEM: 'consent-item',
        DATE_FORMAT_SELECT: 'date-format-select',
        START_PAGE_SELECT: 'start-page-select',
        SWITCH_APP_NOTIFICATIONS: 'switch-app-notifications',
        TIME_FORMAT_SELECT: 'time-format-select',
    },
    SHARED: {
        CAROUSEL_CONTAINER: 'carousel-container',
        CAROUSEL_GRID: 'carousel-grid',
        CAROUSEL_SLIDE: 'carousel-slide',
        CONTENT_EDITOR: 'content-editor',
        CONTENT_EDITOR_COUNTER: 'content-editor-counter',
        EMPTY_RESULT: 'empty-result',
        FILTER_DROPDOWN_MENU: 'filter-dropdown-menu',
        FILTER_DROPDOWN_TOGGLE: 'filter-dropdown-toggle',
        FILTER_MENU_GROUP: 'filter-menu-group',
        FILTER_MENU_OPTION: 'filter-menu-option',
        FILTER_SELECTED_TAGS: 'filter-selected-tags',
        IMAGE_UPLOAD_CONTENT: 'image-upload-content',
        IMAGE_UPLOAD_SUBMIT: 'image-upload-submit',
        LOCALE_SWITCHER: 'locale-switcher',
        PDF_VIEWER: 'pdf-viewer',
        SESSION_EXPIRY_BANNER: 'session-expiry-banner',
        TABLE_EMPTY_ROW: 'table-empty-row',
        TABLE_ROW: 'table-row',
        TABLE_SORT_ASC: 'table-sort-asc',
        TABLE_SORT_DESC: 'table-sort-desc',
        UPLOAD_PROGRESS_BAR: 'upload-progress-bar',
        VIDEO_PLAYER_PLAY: 'video-player-play',
        configurationStep: (label: string) => `admin-center-configuration-${label}`,
        adminTab: (name: string) => `admin-tab-${name}`,
        field: (fieldName: string) => `field-${fieldName}`,
        tableCell: (columnName: string) => `table-cell-${columnName}`,
        tableHeader: (columnName: string) => `table-header-${columnName}`,
    },
    SIDEBAR: {
        CARD_PROGRESS_BAR: 'card-progress-bar',
        CARD_PROGRESS_BAR_FILL: 'card-progress-bar-fill',
        CARD_PROGRESS_BAR_MIN_LEFT: 'card-progress-bar-min-left',
        CARD_PROGRESS_BAR_PERCENT: 'card-progress-bar-percent',
        CARD_PROGRESS_BAR_TRACK: 'card-progress-bar-track',
        LEARNING_STATISTICS: 'learning-statistics',
        LEARNING_STATISTICS_COMPLETED_LABEL: 'learning-statistics-completed-label',
        LEARNING_STATISTICS_COMPLETED_VALUE: 'learning-statistics-completed-value',
        LEARNING_STATISTICS_GRAPH: 'learning-statistics-graph',
        LEARNING_STATISTICS_LEGEND: 'learning-statistics-legend',
        LEARNING_STATISTICS_MONTH_VALUE: 'learning-statistics-month-value',
        LEARNING_STATISTICS_TITLE: 'learning-statistics-title',
        LEARNING_STATISTICS_WEEK_VALUE: 'learning-statistics-week-value',
        PATHWAY_MENU: 'pathway-menu',
        PATHWAY_MENU_CHECKBOX: 'pathway-menu-checkbox',
        PATHWAY_MENU_FINAL_QUIZ: 'pathway-menu-final-quiz',
        PATHWAY_MENU_ITEM: 'pathway-menu-item',
        PATHWAY_MENU_ITEM_DISABLED: 'pathway-menu-item-disabled',
        PATHWAY_MENU_OVERVIEW: 'pathway-menu-overview',
        PATHWAY_MENU_SECTION: 'pathway-menu-section',
        PATHWAY_MENU_SUB_SECTION_TITLE: 'pathway-menu-sub-section-title',
        PATHWAY_MENU_TITLE: 'pathway-menu-title',
    },
    TENANT_CLIENT: {
        ADAPTIVE_TEST_POLICY_HEADER: 'adaptive-test-policy-header',
        ADAPTIVE_TEST_STATE: 'adaptive-test-state',
        ADAPTIVE_TEST_STATE_DESCRIPTION: 'adaptive-test-state-description',
        ADAPTIVE_TEST_SWITCH: 'adaptive-test-switch',
        ADD_CONSENT_ITEM_DESCRIPTION_INPUT: 'add-consent-item-description-input',
        ADD_CONSENT_ITEM_TITLE_INPUT: 'add-consent-item-title-input',
        ADD_RANK_SET_NAME_INPUT: 'add-rank-set-name-input',
        ASSESSOR_CREW_ACTION_MENU: 'assessor-crew-action-menu',
        ASSESSOR_CREW_ADD_BUTTON: 'assessor-crew-add-button',
        ASSESSOR_CREW_TABLE: 'assessor-crew-table',
        ASSESSOR_SHORE_ACTION_MENU: 'assessor-shore-action-menu',
        ASSESSOR_SHORE_ADD_BUTTON: 'assessor-shore-add-button',
        ASSESSOR_SHORE_TABLE: 'assessor-shore-table',
        CANCEL_ICON: 'cancel-icon',
        CLIENT_INFO_BB_REP_EMAIL_INPUT: 'client-info-bb-rep-email-input',
        CLIENT_INFO_BB_REP_NAME_INPUT: 'client-info-bb-rep-name-input',
        CLIENT_INFO_BB_REP_PHONE_INPUT: 'client-info-bb-rep-phone-input',
        CLIENT_INFO_CLIENT_NAME_INPUT: 'client-info-client-name-input',
        CLIENT_INFO_CLIENT_REP_EMAIL_INPUT: 'client-info-client-rep-email-input',
        CLIENT_INFO_CLIENT_REP_NAME_INPUT: 'client-info-client-rep-name-input',
        CLIENT_INFO_CLIENT_REP_PHONE_INPUT: 'client-info-client-rep-phone-input',
        COPY_RANK_SET_NAME_INPUT: 'copy-rank-set-name-input',
        CREW_USERS_ADD_BUTTON: 'crew-users-add-button',
        CREW_USERS_SEARCH_INPUT: 'crew-users-search-input',
        CREW_USERS_TABLE: 'crew-users-table',
        CREW_USER_ACTION_MENU: 'crew-user-action-menu',
        DATA_APPROVER_CREW_ACTION_MENU: 'data-approver-crew-action-menu',
        DATA_APPROVER_CREW_ADD_BUTTON: 'data-approver-crew-add-button',
        DATA_APPROVER_CREW_TABLE: 'data-approver-crew-table',
        DATA_APPROVER_SHORE_ACTION_MENU: 'data-approver-shore-action-menu',
        DATA_APPROVER_SHORE_ADD_BUTTON: 'data-approver-shore-add-button',
        DATA_APPROVER_SHORE_TABLE: 'data-approver-shore-table',
        DATA_PROCESSOR_OFFICER_CREW_ACTION_MENU: 'data-processor-officer-crew-action-menu',
        DATA_PROCESSOR_OFFICER_CREW_ADD_BUTTON: 'data-processor-officer-crew-add-button',
        DATA_PROCESSOR_OFFICER_CREW_TABLE: 'data-processor-officer-crew-table',
        DATA_PROCESSOR_OFFICER_SHORE_ACTION_MENU: 'data-processor-officer-shore-action-menu',
        DATA_PROCESSOR_OFFICER_SHORE_ADD_BUTTON: 'data-processor-officer-shore-add-button',
        DATA_PROCESSOR_OFFICER_SHORE_TABLE: 'data-processor-officer-shore-table',
        EDIT_CONSENT_ITEM_DESCRIPTION_INPUT: 'edit-consent-item-description-input',
        EDIT_CONSENT_ITEM_TITLE_INPUT: 'edit-consent-item-title-input',
        EDIT_ICON: 'edit-icon',
        EXPANDED_ROW_BY_RANK_ONLY_HAS_PRACTICE_QUIZ: 'expanded-row-by-rank-only-has-practice-quiz',
        EXPANDED_ROW_BY_RANK_ONLY_PATHWAY_CATEGORY: 'expanded-row-by-rank-only-pathway-category',
        EXPANDED_ROW_BY_RANK_ONLY_PATHWAY_ID: 'expanded-row-by-rank-only-pathway-id',
        EXPANDED_ROW_BY_RANK_ONLY_RANKS: 'expanded-row-by-rank-only-ranks',
        EXPANDED_ROW_BY_RANK_ONLY_REQUIREMENT_ID: 'expanded-row-by-rank-only-requirement-id',
        EXPANDED_ROW_BY_VESSEL_GROUP_HAS_PRACTICE_QUIZ: 'expanded-row-by-vessel-group-has-practice-quiz',
        EXPANDED_ROW_BY_VESSEL_GROUP_PATHWAY_CATEGORY: 'expanded-row-by-vessel-group-pathway-category',
        EXPANDED_ROW_BY_VESSEL_GROUP_PATHWAY_ID: 'expanded-row-by-vessel-group-pathway-id',
        EXPANDED_ROW_BY_VESSEL_GROUP_RANKS: 'expanded-row-by-vessel-group-ranks',
        EXPANDED_ROW_BY_VESSEL_GROUP_REQUIREMENT_ID: 'expanded-row-by-vessel-group-requirement-id',
        EXPANDED_ROW_BY_VESSEL_GROUP_VESSELS: 'expanded-row-by-vessel-group-vessels',
        EXPANDED_ROW_BY_VESSEL_GROUP_VESSELS_COUNT: 'expanded-row-by-vessel-group-vessels-count',
        EXPANDED_ROW_BY_VESSEL_GROUP_VESSEL_SCOPE: 'expanded-row-by-vessel-group-vessel-scope',
        EXPANDED_ROW_BY_VESSEL_GROUP_VESSEL_SCOPE_ITEM: 'expanded-row-by-vessel-group-vessel-scope-item',
        FLEET_DASHBOARD_ACCESS_ACTION_MENU: 'fleet-dashboard-access-action-menu',
        FLEET_DASHBOARD_ACCESS_ADD_BUTTON: 'fleet-dashboard-access-add-button',
        FLEET_DASHBOARD_ACCESS_TABLE: 'fleet-dashboard-access-table',
        GROUP_TRAINING_ACCESS_ACTION_MENU: 'group-training-access-action-menu',
        GROUP_TRAINING_ACCESS_ADD_BUTTON: 'group-training-access-add-button',
        GROUP_TRAINING_ACCESS_TABLE: 'group-training-access-table',
        MANDATORY_COMPLETION_GATE_DESCRIPTION: 'mandatory-completion-gate-description',
        MANDATORY_COMPLETION_GATE_HEADER: 'mandatory-completion-gate-header',
        MANDATORY_COMPLETION_GATE_SELECT: 'mandatory-completion-gate-select',
        MATRICES_SEARCH_INPUT: 'matrices-search-input',
        MATRICES_TABLE: 'matrices-table',
        MATRIX_PATHWAY_STATUS_PILL: 'matrix-pathway-status-pill',
        MATRIX_PATHWAY_STATUS_PILL_LABEL: 'matrix-pathway-status-pill-label',
        MATRIX_REQUIREMENT_ACTION_MENU: 'matrix-requirement-action-menu',
        REFERENCES_ADD_BUTTON: 'references-add-button',
        REFERENCES_SEARCH_INPUT: 'references-search-input',
        REFERENCES_TABLE: 'references-table',
        REFERENCES_TABLE_SELECT: 'references-table-select',
        SAVE_ICON: 'save-icon',
        SHORE_USERS_ADD_BUTTON: 'shore-users-add-button',
        SHORE_USERS_EMPTY_STATE_ADD_BUTTON: 'shore-users-empty-state-add-button',
        SHORE_USERS_SEARCH_INPUT: 'shore-users-search-input',
        SHORE_USERS_TABLE: 'shore-users-table',
        SHORE_USER_ACTION_MENU: 'shore-user-action-menu',
        SUBSCRIPTIONS_SEARCH_INPUT: 'subscriptions-search-input',
        SUBSCRIPTIONS_TABLE: 'subscriptions-table',
        TENANT_ADMIN_ACTION_MENU: 'tenant-admin-action-menu',
        TENANT_ADMIN_ADD_BUTTON: 'tenant-admin-add-button',
        TENANT_ADMIN_TABLE: 'tenant-admin-table',
        TENANT_CREATOR_ACTION_MENU: 'tenant-creator-action-menu',
        TENANT_CREATOR_ADD_BUTTON: 'tenant-creator-add-button',
        TENANT_CREATOR_TABLE: 'tenant-creator-table',
        TENANT_INFO_ALIAS_INPUT: 'tenant-info-alias-input',
        TENANT_INFO_DATE_FORMAT_SELECT: 'tenant-info-date-format-select',
        TENANT_INFO_NAME_INPUT: 'tenant-info-name-input',
        TENANT_INFO_SHORT_NAME_INPUT: 'tenant-info-short-name-input',
        TENANT_INFO_TIME_FORMAT_SELECT: 'tenant-info-time-format-select',
        TENANT_INFO_TYPE_SELECT: 'tenant-info-type-select',
        VESSELS_ADD_BUTTON: 'vessels-add-button',
        VESSELS_SEARCH_INPUT: 'vessels-search-input',
        VESSELS_TABLE: 'vessels-table',
        VESSEL_ACTION_MENU: 'vessel-action-menu',
        VESSEL_DASHBOARD_ACCESS_ACTION_MENU: 'vessel-dashboard-access-action-menu',
        VESSEL_DASHBOARD_ACCESS_ADD_BUTTON: 'vessel-dashboard-access-add-button',
        VESSEL_DASHBOARD_ACCESS_TABLE: 'vessel-dashboard-access-table',
        ranksLevelOne: (value: string | number) => `ranks-level-one-${value}`,
        ranksLevelTwo: (value: string | number) => `ranks-level-two-${value}`,
        ranksSubItem: (value: string | number) => `ranks-sub-item-${value}`,
        vesselsLevelOne: (value: string | number) => `vessels-level-one-${value}`,
        vesselsLevelTwo: (value: string | number) => `vessels-level-two-${value}`,
        vesselsSubItem: (value: string | number) => `vessels-sub-item-${value}`,
    },
} as const;
